import { useRef, useState } from 'react';
import { Card, Drawer, Flex, Grid, theme, Typography } from 'antd';

import FiltersEcho, { VisibleFilterKey } from './FiltersEcho';
import type { LevelView } from '@/routes/ContentPerformancePage/DataLevelContainer';

type SlidersIconProps = {
  onClick: () => void;
  ariaExpanded?: boolean;
};

const SlidersIcon = ({ onClick, ariaExpanded }: SlidersIconProps) => (
  <button
    data-testid="mobile-filters-toggle"
    type="button"
    aria-label="Open filters"
    aria-expanded={ariaExpanded}
    aria-controls="mobile-filters-overlay"
    onClick={onClick}
  >
    <svg
      width="22"
      height="20"
      viewBox="0 0 22 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M7.21875 12.375C7.77734 12.375 8.25 12.8477 8.25 13.4062V15.125H21.6562C21.8281 15.125 22 15.2969 22 15.4688V16.1562C22 16.3711 21.8281 16.5 21.6562 16.5H8.25V18.2188C8.25 18.8203 7.77734 19.25 7.21875 19.25H5.15625C4.55469 19.25 4.125 18.8203 4.125 18.2188V16.5H0.34375C0.128906 16.5 0 16.3711 0 16.1562V15.4688C0 15.2969 0.128906 15.125 0.34375 15.125H4.125V13.4062C4.125 12.8477 4.55469 12.375 5.15625 12.375H7.21875ZM5.5 13.75V17.875H6.875V13.75H5.5ZM16.8438 6.1875C17.4023 6.1875 17.875 6.66016 17.875 7.21875V8.9375H21.6562C21.8281 8.9375 22 9.10938 22 9.28125V9.96875C22 10.1836 21.8281 10.3125 21.6562 10.3125H17.875V12.0312C17.875 12.6328 17.4023 13.0625 16.8438 13.0625H14.7812C14.1797 13.0625 13.75 12.6328 13.75 12.0312V10.3125H0.34375C0.128906 10.3125 0 10.1836 0 9.96875V9.28125C0 9.10938 0.128906 8.9375 0.34375 8.9375H13.75V7.21875C13.75 6.66016 14.1797 6.1875 14.7812 6.1875H16.8438ZM15.125 7.5625V11.6875H16.5V7.5625H15.125ZM9.96875 0C10.5273 0 11 0.472656 11 1.03125V2.75H21.6562C21.8281 2.75 22 2.92188 22 3.09375V3.78125C22 3.99609 21.8281 4.125 21.6562 4.125H11V5.84375C11 6.44531 10.5273 6.875 9.96875 6.875H7.90625C7.30469 6.875 6.875 6.44531 6.875 5.84375V4.125H0.34375C0.128906 4.125 0 3.99609 0 3.78125V3.09375C0 2.92188 0.128906 2.75 0.34375 2.75H6.875V1.03125C6.875 0.472656 7.30469 0 7.90625 0H9.96875ZM8.25 1.375V5.5H9.625V1.375H8.25Z"
        fill="currentColor"
      />
    </svg>
  </button>
);

type FilterCardProps = {
  visibleFilters: VisibleFilterKey[];
  /**
   * Called when filter selection changes so the tab changes.
   */
  onDataLevelFilterChange?: (level: LevelView) => void;
};

/**
 * A wrapper component that adds sticky scroll behavior to the Filters.
 * When the user scrolls past the filter card, it becomes fixed at the top of the viewport
 * with adjusted styling for better UX. Only applies on large screens (lg breakpoint and above).
 * On pages that show more than the network filter, small screens get a compact bar (date filter + sliders icon).
 */
const FilterCardEcho = ({ visibleFilters, onDataLevelFilterChange }: FilterCardProps) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isLgUp = !!screens.lg;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const isNetworkOnly =
    visibleFilters.length === 1 && visibleFilters[0] === VisibleFilterKey.Networks;

  const fullFiltersBlock = (
    <FiltersEcho visibleFilters={visibleFilters} onDataLevelFilterChange={onDataLevelFilterChange} />
  );

  return (
    <>
      <Flex
        ref={filterRef}
        style={{
          zIndex: 10,
        }}
      >
        <Flex
          vertical
          style={{
            marginBottom: token.marginXL,
            width: '100%',
          }}
        >
          <Card
            styles={{
              root: {
                transition: 'margin 300ms ease, padding 300ms ease, box-shadow 300ms ease',
              },
              body: {
                padding: 0,
                width: '100%',
              },
            }}
          >
            {isNetworkOnly ? (
              fullFiltersBlock
            ) : (
              <>
                <Flex
                  data-testid="sticky-filter-desktop-layout"
                  style={{
                    display: isLgUp ? 'flex' : 'none',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {fullFiltersBlock}
                </Flex>
                <Flex
                  data-testid="sticky-filter-mobile-layout"
                  align="center"
                  gap={token.marginLG}
                  style={{
                    display: isLgUp ? 'none' : 'flex',
                    width: '100%',
                    minWidth: 0,
                    paddingInline: token.paddingLG,
                    paddingBlock: token.padding,
                  }}
                >
                  <Flex flex="1 1 0%" align="center" style={{ minWidth: 0 }}>
                    <FiltersEcho stripOuterPadding visibleFilters={[VisibleFilterKey.DateRange]} />
                  </Flex>
                  <Flex flex="none" align="center" justify="center">
                    <SlidersIcon
                      onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                      ariaExpanded={mobileFiltersOpen}
                    />
                  </Flex>
                </Flex>
              </>
            )}
          </Card>
        </Flex>
      </Flex>
      <Drawer
        open={mobileFiltersOpen}
        placement="bottom"
        data-testid="mobile-filters-overlay"
        size="auto"
        id="mobile-filters-overlay"
        onClose={() => setMobileFiltersOpen(false)}
        title={
          <Typography.Title level={3} style={{ margin: 0 }}>
            Filters
          </Typography.Title>
        }
        styles={{
          body: { paddingTop: 0, paddingBottom: token.padding },
          header: { paddingBottom: 0 },
        }}
      >
        <FiltersEcho
          visibleFilters={visibleFilters.filter((filter) => filter !== VisibleFilterKey.DateRange)}
          onFilterApplied={() => setMobileFiltersOpen(false)}
          onDataLevelFilterChange={onDataLevelFilterChange}
        />
      </Drawer>
    </>
  );
};

export default FilterCardEcho;
